/**
 * Product Area Calculator
 * Enter area, calculate bucket combo (reuses findOptimalCombo from caravan logic), add to cart.
 */
(function () {
  function findOptimalCombo(litresNeeded, variants) {
    if (!variants.length) return [];
    const factor = 1000;
    const needVol = Math.ceil(litresNeeded * factor);
    const biggest = Math.round(variants[0].size * factor);
    const cap = needVol + biggest;

    const dp = new Array(cap + 1).fill(null);
    dp[0] = { cost: 0, counts: new Array(variants.length).fill(0) };

    for (let vol = 0; vol <= cap; vol++) {
      const entry = dp[vol];
      if (!entry) continue;

      variants.forEach((v, i) => {
        const nextVol = vol + Math.round(v.size * factor);
        if (nextVol > cap) return;

        const currentCount = entry.counts[i];
        const isLargest = i === 0;
        if (!isLargest && currentCount >= 3) return;

        const nextCost = entry.cost + v.price;
        const nextCounts = entry.counts.slice();
        nextCounts[i]++;

        const prev = dp[nextVol];
        if (!prev || nextCost < prev.cost) {
          dp[nextVol] = { cost: nextCost, counts: nextCounts };
        }
      });
    }

    let best = null;
    for (let vol = needVol; vol <= cap; vol++) {
      const entry = dp[vol];
      if (!entry) continue;
      if (!best || entry.cost < best.entry.cost) {
        best = { vol, entry };
      }
    }

    if (!best) return [];

    return variants.map((v, i) => ({ ...v, count: best.entry.counts[i] })).filter((v) => v.count > 0);
  }

  function extractBucketVariants(variants, selectedColor) {
    // lrdiy-cartfix: never recommend sold-out variants (they 422 on /cart/add).
    // Variants without an `available` flag are kept for backwards compatibility.
    let filtered = variants.filter((v) => v.available !== false);
    if (selectedColor != null && selectedColor !== '') {
      const colorNorm = (selectedColor || '').toString().trim().toLowerCase();
      filtered = variants.filter((v) => {
        const vColor = (v.color || '').toString().trim().toLowerCase();
        return vColor === colorNorm || vColor.includes(colorNorm) || colorNorm.includes(vColor);
      });
    }
    const bySize = new Map();
    filtered.forEach((v) => {
      const size = Number(v.size);
      if (!size || size <= 0) return;
      const price = Number(v.price) || 0;
      if (!bySize.has(size) || price < bySize.get(size).price) {
        bySize.set(size, {
          size,
          price,
          variantId: v.variantId,
          imageUrl: v.imageUrl || '',
          label: v.label || '',
          color: v.color || '',
        });
      }
    });
    return Array.from(bySize.values()).sort((a, b) => b.size - a.size);
  }

  function formatMoney(cents) {
    if (typeof window.formatMoney === 'function') return window.formatMoney(cents);
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'AUD',
    }).format(cents / 100);
  }

  function init() {
    const containers = document.querySelectorAll('.product-area-calculator[id^="product-area-calc-"]');
    containers.forEach((container) => {
      const calcId = container.id;
      const areaInput = document.getElementById(`${calcId}-area`);
      const resultsEl = document.getElementById(`${calcId}-results`);
      const listEl = document.getElementById(`${calcId}-bucket-list`);
      const addBtn = document.getElementById(`${calcId}-add-btn`);

      if (!areaInput || !resultsEl || !listEl || !addBtn) return;

      const variantsScript = container.querySelector('script[data-product-area-calc-variants]');
      if (!variantsScript) return;

      let variants = [];
      try {
        variants = JSON.parse(variantsScript.textContent || '[]');
      } catch (e) {
        console.warn('Product area calculator: failed to parse variants', e);
        return;
      }

      const hasColorVariants = container.dataset.hasColorVariants === 'true';
      const sectionId = container.dataset.sectionId || '';

      function getSelectedColor() {
        if (!hasColorVariants) return null;
        const productInfo = container.closest('product-info');
        if (!productInfo) return null;
        const variantScript = productInfo.querySelector('variant-selects [data-selected-variant]');
        if (!variantScript?.textContent) return null;
        let variant;
        try {
          variant = JSON.parse(variantScript.textContent);
        } catch (e) {
          return null;
        }
        const ourVariant = variants.find((v) => String(v.variantId) === String(variant.id));
        return ourVariant ? (ourVariant.color || '').trim() || null : null;
      }

      function getBucketVariants() {
        const color = getSelectedColor();
        return extractBucketVariants(variants, hasColorVariants ? color : null);
      }

      const baseCoverageRate = parseFloat(container.dataset.coverageRate) || 1.5;
      const roughCoverageRate = parseFloat(container.dataset.coverageRateRough) || 2;
      const showRoughToggle = container.dataset.showRoughToggle === 'true';
      const productTitle = container.dataset.productTitle || '';
      const unitLabel = container.dataset.unitLabel || 'm²';
      const MAX_AREA = parseFloat(container.dataset.maxArea) || 3000;

      const roughToggle = container.querySelector('[data-rough-surface]');
      const tooltipWrap = container.querySelector('.product-area-calculator__tooltip-wrap');
      const tooltipTrigger = container.querySelector('.product-area-calculator__tooltip-trigger');
      const stepBtns = container.querySelectorAll('.product-area-calculator__step-btn[data-area-calc-action]');

      function parseAreaValue(str) {
        const normalized = (str || '').toString().replace(',', '.');
        const num = parseFloat(normalized);
        return Number.isFinite(num) ? num : 0;
      }

      function getActiveCoverageRate() {
        if (!showRoughToggle || !roughToggle) return baseCoverageRate;
        return roughToggle.checked ? roughCoverageRate : baseCoverageRate;
      }

      function calculate() {
        const rawArea = parseAreaValue(areaInput.value) || 0;
        const area = clampArea(rawArea);
        if (area !== rawArea) {
          areaInput.value = area.toFixed(1);
        }
        if (area <= 0) {
          resultsEl.hidden = true;
          return;
        }

        const coverageRate = getActiveCoverageRate();
        const litresNeeded = Math.round(area * coverageRate * 1000) / 1000;
        const bucketVariants = getBucketVariants();
        if (!bucketVariants.length) {
          resultsEl.hidden = true;
          return;
        }
        const buckets = findOptimalCombo(litresNeeded, bucketVariants);

        if (!buckets.length) {
          resultsEl.hidden = true;
          return;
        }

        listEl.innerHTML = buckets
          .map((b) => {
            const lineTotal = b.count * b.price;
            const formattedPrice = formatMoney(Math.round(lineTotal * 100));
            return `
          <li class="product-area-calculator__bucket-item">
            <img src="${b.imageUrl || ''}" alt="" width="60" height="60" class="product-area-calculator__bucket-image" loading="lazy">
            <div class="product-area-calculator__bucket-info">
              <span class="product-area-calculator__bucket-label">${b.count} × ${b.size}L${b.color ? ` / ${b.color}` : ''}</span>
              <span class="product-area-calculator__bucket-price">${formattedPrice}</span>
            </div>
          </li>
        `;
          })
          .join('');

        resultsEl.hidden = false;
        resultsEl.dataset.buckets = JSON.stringify(buckets);

        const totalCents = buckets.reduce((acc, b) => acc + Math.round(b.count * b.price * 100), 0);
        const formattedTotal = formatMoney(totalCents);
        const baseLabel = addBtn.dataset.addLabel || 'Add to cart';
        const btnText = addBtn.querySelector('.product-area-calculator__add-btn-text');
        if (btnText) btnText.textContent = `${baseLabel} (Total: ${formattedTotal})`;
      }

      const MIN_AREA = 0.1;
      const STEP = 0.1;

      function clampArea(val) {
        return Math.min(MAX_AREA, Math.max(MIN_AREA, val));
      }

      function stepArea(delta) {
        const current = parseAreaValue(areaInput.value) || MIN_AREA;
        const next = clampArea(
          delta < 0
            ? Math.max(MIN_AREA, Math.round((current + delta) * 1000) / 1000)
            : Math.round((current + delta) * 1000) / 1000
        );
        areaInput.value = next.toFixed(1);
        calculate();
      }

      function setupHoldButton(button, callback) {
        let holdTimeout;
        let holdInterval;
        const startHold = () => {
          callback();
          holdTimeout = setTimeout(() => {
            holdInterval = setInterval(callback, 75);
          }, 300);
        };
        const clearHold = () => {
          clearTimeout(holdTimeout);
          clearInterval(holdInterval);
        };
        button.addEventListener('mousedown', startHold);
        button.addEventListener('mouseup', clearHold);
        button.addEventListener('mouseleave', clearHold);
        button.addEventListener('touchstart', startHold, { passive: true });
        button.addEventListener('touchend', clearHold);
      }

      stepBtns.forEach((btn) => {
        const action = btn.dataset.areaCalcAction;
        if (action === 'decrease') {
          setupHoldButton(btn, () => stepArea(-STEP));
        } else if (action === 'increase') {
          setupHoldButton(btn, () => stepArea(STEP));
        }
      });

      areaInput.addEventListener('focus', (e) => e.target.select());
      areaInput.addEventListener('click', (e) => e.target.select());
      areaInput.addEventListener('input', calculate);
      areaInput.addEventListener('change', calculate);
      if (roughToggle) {
        roughToggle.addEventListener('change', calculate);
      }

      if (hasColorVariants && sectionId && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId === sectionId && container.isConnected) {
            calculate();
          }
        });
      }

      if (tooltipTrigger && tooltipWrap) {
        tooltipTrigger.addEventListener('click', (e) => {
          e.preventDefault();
          tooltipWrap.classList.toggle('active');
        });
        document.addEventListener('click', (e) => {
          if (!tooltipWrap.contains(e.target)) {
            tooltipWrap.classList.remove('active');
          }
        });
      }

      calculate();

      addBtn.addEventListener('click', async () => {
        const buckets = JSON.parse(resultsEl.dataset.buckets || '[]');
        if (!buckets.length) return;

        const items = buckets.map((b) => ({
          id: typeof b.variantId === 'string' ? b.variantId : String(b.variantId),
          quantity: b.count,
        }));

        addBtn.disabled = true;
        addBtn.classList.add('loading');
        const spinner = addBtn.querySelector('.loading__spinner');
        const btnText = addBtn.querySelector('.product-area-calculator__add-btn-text');
        if (spinner) spinner.classList.remove('hidden');
        if (btnText) btnText.classList.add('hidden');

        try {
          // Use same cart detection as Dawn product-form
          const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          const routesObj = typeof routes !== 'undefined' ? routes : window?.routes;
          const cartAddUrl = routesObj?.cart_add_url || '/cart/add.js';

          // No cart UI (e.g. cart_type = page) — redirect to cart
          if (!cartEl) {
            window.location = routesObj?.cart_url || '/cart';
            return;
          }

          cartEl.setActiveElement?.(addBtn || document.activeElement);

          // lrdiy-cartfix: add all buckets in ONE /cart/add.js request using the
          // `items` array. Batched adds are all-or-nothing, so a sold-out
          // variant can't leave a partial kit in the cart like the old
          // one-request-per-item loop could.
          const payload = {
            items: items.map((item) => ({ id: Number(String(item.id).split('/').pop()), quantity: item.quantity })),
          };
          if (cartEl?.getSectionsToRender) {
            payload.sections = cartEl
              .getSectionsToRender()
              .map((s) => s.id)
              .join(',');
            payload.sections_url = window.location.pathname;
          }

          const config = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
          };

          const res = await fetch(cartAddUrl, config);
          const json = await res.json().catch(() => null);
          if (!res.ok || json?.status) {
            console.error('Cart add failed:', { status: res.status, body: json });
            if (typeof publish === 'function' && PUB_SUB_EVENTS?.cartError) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-area-calculator',
                productVariantId: items[0]?.id,
                errors: json?.errors || json?.description,
                message: json?.message,
              });
            }
            // Surface Shopify's real reason (e.g. sold-out product) to the customer
            throw new Error(json?.description || json?.message || 'Error adding to cart. Please try again.');
          }
          const lastResponse = json;

          // Dawn pattern: publish cartUpdate first (lets subscribers run), then render
          if (typeof publish === 'function' && PUB_SUB_EVENTS?.cartUpdate) {
            const startMarker = typeof CartPerformance?.createStartingMarker === 'function'
              ? CartPerformance.createStartingMarker('add:wait-for-subscribers')
              : null;
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-area-calculator',
              productVariantId: items[items.length - 1]?.id,
              cartData: lastResponse || {},
            });
            if (startMarker && typeof CartPerformance?.measureFromMarker === 'function') {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            }
          }

          // Render cart drawer/notification with sections (Dawn default)
          if (cartEl?.renderContents && lastResponse?.sections) {
            if (typeof CartPerformance?.measure === 'function') {
              CartPerformance.measure('add:paint-updated-sections', () => {
                cartEl.renderContents(lastResponse);
              });
            } else {
              cartEl.renderContents(lastResponse);
            }
            if (cartEl.classList?.contains?.('is-empty')) {
              cartEl.classList.remove('is-empty');
            }
          } else if (cartEl && !lastResponse?.sections) {
            // No sections returned — redirect to cart
            window.location = routesObj?.cart_url || '/cart';
          }
        } catch (err) {
          console.error('Product area calculator add to cart:', err);
          alert(err.message || 'Error adding to cart. Please try again.');
        } finally {
          addBtn.disabled = false;
          addBtn.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
          if (btnText) btnText.classList.remove('hidden');
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
